import React, { useContext, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { userContext } from "../../../../stores/users/userContext";
import { ROUTES } from "../../../../constants";
import logoDark from "../../../../assets/brand/logo_site.svg";
import logoLight from "../../../../assets/brand/logo_site_onlight.svg";
import UploadDownloadArea from "./UploadDownloadArea";
import HeroClipDemo from "./HeroClipDemo";
import FlagshipLiveEt from "./FlagshipLiveEt";
import FlagshipTwoPhones from "./FlagshipTwoPhones";
import FlagshipPhoneToTimeline from "./FlagshipPhoneToTimeline";
import { SelectClipsLoop, BlackCherryLoop } from "./FlagshipTwoWaysDemos";
import { CONNECTOR_HOME_BAND } from "../../../connector/connectorGuideConstants";

/**
 * Home Layout "R" — the FLAGSHIP landing page (v6, "clean split").
 *
 * Clean + award-simple: prominent theme-aware logo, a rotating headline (animated strike-through
 * "Read the transcript → Cherry-pick the clips" + a highlighter sweep + the client's copy lines), a
 * WIDE real live ET (like Layout O), a drag/drop-or-paste band, a phone carousel, and a dedicated
 * Premiere-plugin section (CherryClipz ✚ Pr, the panel-in-Premiere recreation + the phone link).
 * Signature = a light/dark toggle. Cherry used sparingly.
 *
 * ISOLATION: styles scoped under `.ccfp` (theme vars under `.ccfp[data-theme=...]`, on the root div,
 * never <html>). Live pieces are flagship-scoped copies of the O/K patterns; originals untouched.
 */

const CheckSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12l5 5L20 6" />
  </svg>
);
const SunSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.7} strokeLinecap="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
  </svg>
);
const MoonSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);
const GoogleG: React.FC = () => (
  <svg viewBox="0 0 48 48" width="15" height="15" aria-hidden style={{ display: "block" }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
const PrLogo: React.FC = () => (
  <span className="prlogo" aria-label="Premiere Pro">
    Pr
  </span>
);

// Rotating hero headlines. [0] = animated strike-through device; [1] = highlighter sweep.
export const HEADLINES: React.ReactNode[] = [
  <>Cherry-pick <span className="rw">your clips</span>.</>,
  <>
    <span className="strike-anim">Read the transcript.</span>
    <br />
    <span className="rw">Cherry-pick</span> the clips.
  </>,
  <>
    The <span className="rw">spreadsheet</span>
    <br />
    for video.
  </>,
  <>
    Any clip in just <span className="hl">one click</span>.
  </>,
  <>
    Your footage,
    <br />
    <span className="rw">fully searchable</span>.
  </>,
  // Puts the Premiere plugin above the fold without giving it a whole section — an editor
  // scanning the hero should learn it exists in the four seconds this line is up.
  <>
    Cherry-pick straight into
    <br />
    <span className="rw">Premiere Pro</span>.
  </>,
  <>
    A picture is worth
    <br />
    <span className="rw">a thousand words</span>.
  </>,
];

// Two switchable views of "phone → Premiere": a LIVE demo (a phone auto-ticks clips and they populate
// a Premiere-style timeline) and the REAL panel screenshot. The screenshot is one of a few options.
const PremiereShowcase: React.FC = () => {
  const [tab, setTab] = useState<"live" | "shot">("live");
  const [realImg, setRealImg] = useState(true);
  return (
    <div className="prem-wrap">
      <div className="prem-tabs">
        <button className={`prem-tab${tab === "live" ? " on" : ""}`} onClick={() => setTab("live")}>
          ▶ Live demo
        </button>
        <button className={`prem-tab${tab === "shot" ? " on" : ""}`} onClick={() => setTab("shot")}>
          🖥 The real panel
        </button>
      </div>

      {tab === "live" ? (
        <div className="prem-live">
          <FlagshipPhoneToTimeline />
        </div>
      ) : (
        <div className="prem-screen-wide">
          {realImg ? (
            <img
              className="prem-realimg"
              src="/premiere-real.png"
              alt="CherryClipz plugin inside Premiere Pro"
              onError={() => setRealImg(false)}
            />
          ) : (
            <div className="pw">
              <div className="pw-bar">
                <span className="pw-dots">
                  <i />
                  <i />
                  <i />
                </span>
                <span>Adobe Premiere Pro — CherryClipz Plugin</span>
              </div>
              <div className="pw-tl">
                <div className="pw-tl-head mono">Sequence 02 · your ticked clips landed here</div>
                <div className="pw-ruler mono">
                  <span>00:00</span>
                  <span>00:15</span>
                  <span>00:30</span>
                  <span>00:45</span>
                </div>
                <div className="pw-tl-body">
                  <span className="pw-playhead" />
                  {[
                    { l: "V1", wave: false },
                    { l: "A1", wave: true },
                  ].map((tr) => (
                    <div key={tr.l} className="pw-track">
                      <span className="pw-track-l mono">{tr.l}</span>
                      <div className="pw-clips">
                        {[74, 44, 96, 58].map((w, i) => (
                          <span key={i} className={`pw-clip${i < 2 ? " hot" : ""}${tr.wave ? " wave" : ""}`} style={{ width: `${w}px` }} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

type Theme = "light" | "dark";
const THEME_KEY = "cc_flagship_theme";

/** The two live store listings (both apps in production since 2026-09-28). */
const APP_STORE_URL = "https://apps.apple.com/us/app/cherryclipz/id6810239977";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.cherryclipz.app";


/**
 * The YouTube video id for the demo reel. Empty until the video is shot — the section then
 * renders a placeholder showing exactly where it will sit, so the layout can be judged now.
 * Set this one string and it becomes a real embed.
 */
export const DEMO_YOUTUBE_ID = "_3jNY4eGSUk";

interface FlagshipPageProps {
  /** Layout T: same page as R, plus the "watch it work" video under the hero. */
  withDemoVideo?: boolean;
}

const FlagshipPage: React.FC<FlagshipPageProps> = ({ withDemoVideo = true }) => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme>("light");
  const [hi, setHi] = useState(0);
  const logo = theme === "light" ? logoLight : logoDark;

  useEffect(() => {
    try {
      const s = localStorage.getItem(THEME_KEY);
      if (s === "light" || s === "dark") setTheme(s);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    } catch (e) {
      /* ignore */
    }
    const t = window.setInterval(() => setHi((i) => (i + 1) % HEADLINES.length), 4200);
    return () => window.clearInterval(t);
  }, []);

  const toggleTheme = () =>
    setTheme((t) => {
      const next: Theme = t === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch (e) {
        /* ignore */
      }
      return next;
    });

  const scrollTo = (id: string) => {
    try {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) {
      /* ignore */
    }
  };
  const goLibrary = () => navigate(ROUTES.MY_LIBRARY);
  // The nav said "Sign in" whether or not you were signed in — so you had no way to tell,
  // and it pointed at the library, which just bounces you to login if you're logged out.
  const userStore = useContext(userContext);
  const isAuthed = userStore.isUserAuth();
  const goSignIn = () => navigate(ROUTES.LANDING);
  const goPricing = () => navigate(ROUTES.PRICING);
  const goPluginPage = () => navigate(ROUTES.PLUGIN);
  const goAppPage = () => navigate(ROUTES.APP);
  const goPluginDownload = () => navigate(ROUTES.PLUGIN_DOWNLOAD);
  const goAiGuide = () => navigate(ROUTES.AI_CONNECTOR);

  const Brand: React.FC<{ size?: number }> = ({ size = 24 }) => (
    <>
      <img className="cherry-mark" src={logo} alt="" height={size} style={{ width: "auto" }} />
      <span className="wordmark">
        Cherry<span className="ck">Clipz</span>
      </span>
    </>
  );

  return (
    <div className="ccfp" data-theme={theme} id="ccfp-top">
      <style>{FLAGSHIP_CSS}</style>

      {/* NAV */}
      <nav>
        <div className="wrap nav-in">
          <button className="brand brand-btn" onClick={() => scrollTo("ccfp-top")} aria-label="CherryClipz home">
            <Brand size={30} />
          </button>
          <div className="nav-links">
            <button className="linkbtn" onClick={() => scrollTo("editor")}>
              The editor
            </button>
            <button className="linkbtn" onClick={() => scrollTo("phone")}>
              On your phone
            </button>
            <button className="linkbtn" onClick={goAppPage}>
              Get the app
            </button>
            <button className="linkbtn" onClick={goPluginPage}>
              Premiere plugin
            </button>
            <button className="linkbtn" onClick={goLibrary}>
              My library
            </button>
            <button className="linkbtn" onClick={() => scrollTo("pricing")}>
              Pricing
            </button>
          </div>
          <div className="nav-right">
            <button
              className="toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              title="Light / dark"
            >
              <span className="sun">
                <SunSvg />
              </span>
              <span className="moon">
                <MoonSvg />
              </span>
            </button>
            <button className="signin" onClick={isAuthed ? goLibrary : goSignIn}>
              {isAuthed ? (
                <>
                  <span className="dot" aria-hidden />
                  <span className="signin-full">
                    {isAuthed && userStore.user ? userStore.getUserEmail() : "My account"}
                  </span>
                  <span className="signin-short">Library</span>
                </>
              ) : (
                <>
                  <GoogleG />
                  Sign in
                </>
              )}
            </button>
            <button className="btn btn-cherry nav-cta" onClick={() => scrollTo("upload")}>
              Try it free
            </button>
          </div>
        </div>
      </nav>

      {/* HERO — text left, WIDE real ET right */}
      <header className="hero" id="editor">
        <div className="wrap hero-grid">
          <div className="hero-copy">
            <span className="eyebrow">The spreadsheet for video</span>
            <h1 key={hi} className="rot">
              {HEADLINES[hi]}
            </h1>
            <p className="sub">
              Every sentence in your video becomes a clip with its own thumbnail. See the moments, cherry-pick the
              ones you want, export in a click.
            </p>
            <div className="cta">
              <button className="btn btn-cherry" onClick={() => scrollTo("upload")}>
                Try it free
              </button>
              <button className="btn btn-ghost" onClick={() => scrollTo("premiere")}>
                See the Premiere plugin
              </button>
            </div>
            <div className="fine mono">No login to try · Free for your first hour</div>
            <div className="hero-stores">
              <span className="hero-stores-label mono">Now available on iPhone &amp; Android</span>
              <div className="hero-badges">
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Download CherryClipz on the App Store">
                  {/* Apple ships a black badge for light backgrounds and a white one for dark. */}
                  <img
                    className="b-apple"
                    src={theme === "light" ? "/app/appstore-badge-black.svg" : "/app/appstore-badge-white.svg"}
                    alt="Download on the App Store"
                  />
                </a>
                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Get CherryClipz on Google Play">
                  <img className="b-play" src="/app/googleplay-badge.png" alt="Get it on Google Play" />
                </a>
              </div>
            </div>
          </div>
          <div className="hero-et">
            <FlagshipLiveEt title="Film Reel — live" defaultMode="desktop" heightPx={560} defaultZoom={0.82} />
          </div>
        </div>
      </header>

      {/* DRAG / DROP / PASTE band */}
      {withDemoVideo && (
        <section className="demo" id="watch">
          <div className="wrap">
            <div className="demo-head">
              <span className="eyebrow">Watch it work</span>
              <h2>The CherryClipz spreadsheet, inside Premiere Pro.</h2>
              <p>Controlled from your phone. Tick a line, the clip lands on your timeline.</p>
            </div>
            <div className="demo-frame">
              {DEMO_YOUTUBE_ID ? (
                <iframe
                  src={`https://www.youtube.com/embed/${DEMO_YOUTUBE_ID}`}
                  title="CherryClipz demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="demo-ph">
                  <span className="demo-ph-title">Demo video goes here</span>
                  <span className="demo-ph-note">
                    Set DEMO_YOUTUBE_ID in FlagshipPage.tsx once the reel is up.
                  </span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="drop" id="upload">
        <div className="wrap">
          <div className="drop-head">
            <img className="drop-logo" src={logo} alt="CherryClipz" height={46} style={{ width: "auto" }} />
            <span className="eyebrow">Your turn</span>
            <h2>You've seen the demo. Now make it <span className="rw">yours</span>.</h2>
            <p>Drop your own footage or paste a YouTube, Google Drive or Dropbox link — watch every sentence turn into a clip you can pick, in seconds.</p>
          </div>
          <div id="cc-demo-upload" className="drop-box">
            <UploadDownloadArea />
          </div>
          <div className="drop-demo" aria-hidden>
            <HeroClipDemo />
          </div>
        </div>
      </section>

      {/* THREE QUIET STEPS */}
      <section className="steps">
        <div className="wrap steps-in">
          <div className="step">
            <div className="k mono">01</div>
            <h3>Upload</h3>
            <p>A file or a link. We read every line and give it a thumbnail.</p>
          </div>
          <div className="step">
            <div className="k mono">02</div>
            <h3>Cherry-pick</h3>
            <p>Search, filter by speaker or feeling, and check the moments you want.</p>
          </div>
          <div className="step">
            <div className="k mono">03</div>
            <h3>Export</h3>
            <p>One reel, or one clip. MP4, captions, or straight to your timeline.</p>
          </div>
        </div>
      </section>

      {/* TWO PHONES — pick on the editor, watch/reframe on the feed */}
      {/* Pick them yourself, or ask Cherry. The two ways to cut, shown rather than described. */}
      <section className="ways" id="ways">
        <div className="wrap">
          <div className="ways-head">
            <span className="eyebrow">Two ways to cut</span>
            <h2>
              Choose<span className="cw-dot">.</span>
            </h2>
            <p>Pick the clips yourself, or tell Black Cherry what you're after and let it pick.</p>
          </div>
          <div className="pills">
            <div className="way">
              <span className="way-tag red">Human choice</span>
              <div className="pill red">
                <h3>CherryPick</h3>
                <span className="pill-kind red mono">You choose</span>
                <p>Search, filter and tick the lines you want. The number is the order they cut in.</p>
                <div className="pill-demo">
                  <SelectClipsLoop />
                </div>
              </div>
            </div>
            <div className="way">
              <span className="way-tag pl">AI prompt</span>
              <div className="pill purple">
                <h3>Black Cherry</h3>
                <span className="pill-kind pl mono">Cherry chooses</span>
                <p>Describe the reel you want. Cherry reads the sheet and picks the clips for you.</p>
                <div className="pill-demo">
                  <BlackCherryLoop />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLAUDE + CHATGPT — the connector in one line, and the way to the /ai guide */}
      <section className="aiband" id="ai">
        <div className="wrap aiband-in">
          <div>
            <span className="eyebrow">{CONNECTOR_HOME_BAND.eyebrow}</span>
            <h2>{CONNECTOR_HOME_BAND.heading}</h2>
            <p>{CONNECTOR_HOME_BAND.body}</p>
          </div>
          <button className="btn btn-ghost" onClick={goAiGuide}>
            {CONNECTOR_HOME_BAND.cta} →
          </button>
        </div>
      </section>

      <section className="phones" id="phone">
        <div className="wrap">
          <div className="ph-head">
            <span className="eyebrow">In your pocket</span>
            <h2>Edit with your thumb.</h2>
            <p className="ph-sub">
              Pick on one phone, watch it play on the other. No more <span className="crossed">scrubbing</span> a timeline.
            </p>
          </div>
          <FlagshipTwoPhones />
          {/* The section shows the phone; this is where you go and get it. */}
          <div className="prem-cta">
            <button className="btn btn-cherry" onClick={goAppPage}>
              Get the app
            </button>
          </div>
        </div>
      </section>

      {/* PREMIERE PLUGIN — dedicated section */}
      <section className="prem" id="premiere">
        <div className="wrap">
          <div className="prem-lockup">
            <img className="cc-mark" src={logo} alt="CherryClipz" height={58} style={{ width: "auto" }} />
            <span className="plus">+</span>
            <PrLogo />
          </div>
          <div className="sec-head">
            <span className="eyebrow">Premiere Pro plugin</span>
            <h2>Cherry-pick on your phone. It cuts in Premiere.</h2>
            <p>
              The CherryClipz panel lives right inside Premiere Pro. Turn on phone control, open the link on your
              phone, and every clip you tick drops onto your open sequence as native media — live.
            </p>
          </div>
          <PremiereShowcase />
          <p className="prem-note mono">
            ↑ the real CherryClipz panel, running inside Premiere Pro.
          </p>
          {/* The Premiere section explains the plugin; this is how you go and see it. */}
          <div className="prem-cta">
            <button className="btn btn-cherry" onClick={goPluginPage}>
              See the Premiere plugin
            </button>
            <button className="btn btn-ghost" onClick={goPluginDownload}>
              Get the panel
            </button>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="price" id="pricing">
        <div className="wrap">
          <span className="eyebrow">Pricing</span>
          <h2 className="price-h">Free to start. Pro when you're rolling.</h2>
          <div className="cards">
            <div className="card">
              <div className="plan mono">Free</div>
              <div className="amt">
                $0<span> / mo</span>
              </div>
              <div className="desc">Everything you need to try the whole thing.</div>
              <ul>
                <li>
                  <CheckSvg />1 free hour of transcription to start
                </li>
                <li>
                  <CheckSvg />10 GB · 50 exports a month
                </li>
                <li>
                  <CheckSvg />The full editor on the web
                </li>
                <li>
                  <CheckSvg />The phone app, on iPhone and Android
                </li>
                <li>
                  <CheckSvg />The Premiere Pro panel, on Mac and Windows
                </li>
              </ul>
              <button className="btn btn-ghost" onClick={() => scrollTo("upload")}>
                Try it free
              </button>
            </div>
            {/* MUST MATCH /pricing/ (SubscriptionPlans.jsx DEFAULT_PLAN_FEATURES) and the
                ResourcesByPlan rows in admin. Three plans since 2026-09-08. */}
            <div className="card">
              <div className="plan mono">Creator</div>
              <div className="amt">
                $10<span> / mo</span>
              </div>
              <div className="desc">For anyone cutting more than an hour.</div>
              <ul>
                <li>
                  <CheckSvg />5 hours of transcription
                </li>
                <li>
                  <CheckSvg />20 GB · 100 exports a month
                </li>
                <li>
                  <CheckSvg />Everything in Free, five times the hours
                </li>
                <li>
                  <CheckSvg />Web, phone app and Premiere panel
                </li>
              </ul>
              <button className="btn btn-ghost" onClick={goPricing}>
                Get Creator
              </button>
            </div>
            <div className="card pro">
              <div className="plan mono">Pro</div>
              <div className="amt">
                $29<span> / mo</span>
              </div>
              <div className="desc">For creators shipping every week.</div>
              <ul>
                <li>
                  <CheckSvg />15 hours of transcription
                </li>
                <li>
                  <CheckSvg />100 GB · 500 exports a month
                </li>
                <li>
                  <CheckSvg />Priority support, a real person, usually same day
                </li>
                <li>
                  <CheckSvg />Web, phone app and Premiere panel
                </li>
              </ul>
              <button className="btn btn-cherry" onClick={goPricing}>
                Go Pro
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSE */}
      <section className="close">
        <div className="wrap">
          <h2>Start with one video.</h2>
          <p>See every line. Cherry-pick the ones worth keeping.</p>
          <button className="btn btn-cherry" onClick={() => scrollTo("upload")}>
            Try it free
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="wrap foot">
          <button className="brand brand-btn" onClick={() => scrollTo("ccfp-top")} aria-label="CherryClipz home">
            <Brand size={24} />
          </button>
          <div className="fl">
            <button className="linkbtn" onClick={() => scrollTo("editor")}>
              Editor
            </button>
            <button className="linkbtn" onClick={() => scrollTo("phone")}>
              Phone
            </button>
            <button className="linkbtn" onClick={() => scrollTo("premiere")}>
              Premiere
            </button>
            <button className="linkbtn" onClick={goLibrary}>
              My library
            </button>
            <button className="linkbtn" onClick={() => scrollTo("pricing")}>
              Pricing
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ── Scoped stylesheet (all selectors under `.ccfp`; theme vars under `.ccfp[data-theme=...]`) ─────
const FLAGSHIP_CSS = `
.ccfp[data-theme="light"]{
  --bg:#f4f1ee; --surface:#ffffff; --surface-2:#faf7f4; --line:#e9e3dd; --line-2:#ddd5ce;
  --ink:#181315; --muted:#6a5f65; --faint:#8a7f86; --cherry:#e01e49; --cherry-soft:#ec5578;
  --shadow:0 30px 60px -34px rgba(60,20,30,.28);
}
.ccfp[data-theme="dark"]{
  --bg:#0e0b0d; --surface:#161114; --surface-2:#1b1418; --line:#282025; --line-2:#38292f;
  --ink:#f5edf0; --muted:#a99ca2; --faint:#7a6d73; --cherry:#ec2b57; --cherry-soft:#ff6f8f;
  --shadow:0 40px 80px -40px rgba(0,0,0,.8);
}
.ccfp{
  --sans: system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  --mono: ui-monospace,"SF Mono",Menlo,Consolas,monospace;
  position:relative; min-height:100vh; background:var(--bg); color:var(--ink);
  font-family:var(--sans); line-height:1.5; -webkit-font-smoothing:antialiased;
  transition:background .35s ease, color .35s ease;
}
@media (prefers-reduced-motion: reduce){ .ccfp{transition:none} }
.ccfp *{box-sizing:border-box}
.ccfp .wrap{max-width:1140px;margin:0 auto;padding:0 28px;position:relative}
.ccfp .mono{font-family:var(--mono)}
.ccfp .linkbtn{font-family:inherit;background:none;border:none;padding:0;margin:0;cursor:pointer;color:inherit}
.ccfp .brand-btn{background:none;border:none;cursor:pointer;padding:0;display:flex;align-items:center;gap:10px}

.ccfp h1,.ccfp h2,.ccfp h3{margin:0;font-weight:770;letter-spacing:-0.03em;line-height:1.03;text-wrap:balance}
.ccfp .eyebrow{font-family:var(--mono);font-size:12px;letter-spacing:.22em;text-transform:uppercase;color:var(--faint)}

.ccfp .btn{display:inline-flex;align-items:center;gap:8px;font-family:var(--sans);font-weight:650;font-size:15px;border-radius:999px;padding:13px 24px;border:1px solid transparent;cursor:pointer;transition:transform .14s ease, background .2s ease, border-color .2s ease, color .2s ease, filter .2s ease;background:transparent;color:var(--ink)}
.ccfp .btn-cherry{background:var(--cherry);color:#fff}
.ccfp .btn-cherry:hover{transform:translateY(-1px);filter:brightness(1.06)}
.ccfp .btn-ghost{border-color:var(--line-2);color:var(--ink)}
.ccfp .btn-ghost:hover{border-color:var(--cherry)}
@media (prefers-reduced-motion: reduce){ .ccfp .btn:hover{transform:none} }
.ccfp :focus-visible{outline:2px solid var(--cherry);outline-offset:3px;border-radius:8px}

/* NAV + BRAND */
.ccfp nav{position:sticky;top:0;z-index:20;background:color-mix(in srgb, var(--bg) 82%, transparent);backdrop-filter:blur(12px);border-bottom:1px solid var(--line)}
.ccfp .nav-in{display:flex;align-items:center;gap:22px;height:70px}
.ccfp .cherry-mark{flex:none;display:block}
.ccfp .wordmark{font-weight:820;font-size:21px;letter-spacing:-.02em;color:var(--ink)}
.ccfp .wordmark .ck{color:var(--cherry)}
.ccfp .nav-links{display:flex;gap:22px;margin-left:14px}
.ccfp .nav-links .linkbtn{font-size:14px;color:var(--muted);font-weight:550;white-space:nowrap}
.ccfp .nav-links .linkbtn:hover{color:var(--ink)}
.ccfp .nav-right{margin-left:auto;display:flex;align-items:center;gap:14px;flex:none}
.ccfp .nav-right .btn{white-space:nowrap}
/* A phone has no room for the theme switch, Sign in AND this button: it was pushed off the right
   edge. The hero's own "Try it free" sits right under the nav, so the nav copy goes on phones. */
@media (max-width:560px){ .ccfp .nav-cta{display:none} }
.ccfp .signin{display:inline-flex;align-items:center;gap:7px;font-family:var(--sans);font-size:14px;color:var(--muted);font-weight:600;background:var(--surface);border:1px solid var(--line-2);border-radius:999px;padding:7px 14px;cursor:pointer}
.ccfp .signin .dot{width:7px;height:7px;border-radius:50%;background:#33d17a;flex:none}
.ccfp .signin:hover{color:var(--ink);border-color:var(--cherry)}
.ccfp .toggle{display:inline-flex;align-items:center;gap:0;border:1px solid var(--line-2);border-radius:999px;padding:3px;background:var(--surface-2);cursor:pointer}
.ccfp .toggle span{display:grid;place-items:center;width:30px;height:26px;border-radius:999px;color:var(--faint)}
.ccfp .toggle span svg{width:15px;height:15px;stroke:currentColor;fill:none;display:block}
.ccfp[data-theme="light"] .toggle .sun{background:var(--surface);color:var(--cherry);box-shadow:0 1px 3px rgba(0,0,0,.08)}
.ccfp[data-theme="dark"] .toggle .moon{background:#241a1f;color:var(--cherry-soft)}
.ccfp .signin-short{display:none}
/* Below 1040px the nav links go away, but the signin/account button MUST NOT — on a phone it
   is the only route from the flagship to your library. Hiding it stranded signed-in users
   with no way to reach their own clips. The email is too wide for a phone, so it collapses
   to "Library", which is what the button actually does once you are signed in. */
@media (max-width:1240px){
  .ccfp .nav-links{display:none}
  .ccfp .signin{padding:7px 12px;font-size:13px}
  .ccfp .signin-full{display:none}
  .ccfp .signin-short{display:inline}
}

/* HERO — wide ET (like Layout O) */
.ccfp .hero{padding:56px 0 40px}
.ccfp .hero-grid{display:grid;grid-template-columns:0.72fr 1.28fr;gap:44px;align-items:center}
.ccfp .hero-copy{min-width:0}
.ccfp .hero-copy .eyebrow{display:inline-block;margin-bottom:18px}
.ccfp .hero h1.rot{font-size:clamp(36px,4.8vw,60px);min-height:2.9em}
@media (prefers-reduced-motion: no-preference){ .ccfp .hero h1.rot{animation:ccfp-fade .55s ease} }
@keyframes ccfp-fade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.ccfp .hero .sub{max-width:440px;margin:20px 0 0;color:var(--muted);font-size:clamp(15px,1.4vw,17px)}
.ccfp .hero .cta{display:flex;gap:12px;margin-top:26px;flex-wrap:wrap}
.ccfp .hero .fine{margin-top:16px;font-size:12px;color:var(--faint);letter-spacing:.02em}
.ccfp .hero-et{min-width:0}
.ccfp .hero-stores{margin-top:22px;padding-top:18px;border-top:1px solid var(--line)}
.ccfp .hero-stores-label{display:block;margin-bottom:10px;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--cherry)}
.ccfp .hero-badges{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.ccfp .hero-badges a{display:inline-flex;align-items:center;transition:transform .15s ease}
.ccfp .hero-badges a:hover{transform:translateY(-2px)}
.ccfp .hero-badges .b-apple{height:40px;width:auto;display:block}
/* Google's badge carries its own clear space, so it is set larger and pulled in to match Apple's. */
.ccfp .hero-badges .b-play{height:59px;width:auto;display:block;margin:-9.5px -8.5px}
@media (max-width:960px){ .ccfp .hero-grid{grid-template-columns:1fr;gap:32px} .ccfp .hero-et{order:2} .ccfp .hero h1.rot{min-height:2.4em} }

/* animated strike-through (native line-through so it wraps, never clips) + highlighter */
/* strike-through DRAWS across (a scaling line), so you watch it get crossed out */
/* NATIVE line-through: the browser strikes THROUGH each glyph at the correct height, and — crucially —
   crosses out BOTH lines when "Read the transcript." wraps (an absolutely-positioned ::after bar can't:
   it sat between the two wrapped lines and read as an underline). The strike fades in (decoration-color
   transparent -> cherry) so you still watch the words get crossed out. */
.ccfp .strike-anim{color:var(--faint);text-decoration:line-through;text-decoration-thickness:5px;text-underline-offset:0;-webkit-text-decoration-color:transparent;text-decoration-color:transparent}
@media (prefers-reduced-motion: no-preference){ .ccfp .hero h1.rot .strike-anim{animation:ccfp-strike .5s .45s forwards ease} }
@media (prefers-reduced-motion: reduce){ .ccfp .strike-anim{-webkit-text-decoration-color:var(--cherry);text-decoration-color:var(--cherry)} }
@keyframes ccfp-strike{to{-webkit-text-decoration-color:var(--cherry);text-decoration-color:var(--cherry)}}
/* red-word emphasis inside the rotating headline */
.ccfp .rw{color:var(--cherry)}
.ccfp .hl{position:relative;white-space:nowrap;z-index:0}
.ccfp .hl::before{content:"";position:absolute;left:-3px;right:-3px;top:12%;bottom:12%;background:color-mix(in srgb, var(--cherry) 26%, transparent);border-radius:3px;z-index:-1;transform:scaleX(0);transform-origin:left}
@media (prefers-reduced-motion: no-preference){ .ccfp .hero h1.rot .hl::before{animation:ccfp-hl .5s .5s forwards ease} }
@media (prefers-reduced-motion: reduce){ .ccfp .hl::before{transform:scaleX(1)} }
@keyframes ccfp-hl{to{transform:scaleX(1)}}

/* DRAG & DROP */
/* Layout T: the demo reel, sitting between the live editor and the upload box. 16:9,
   fluid, and the placeholder holds the same footprint so the page reads correctly before
   the video exists. */
/* two ways to cut — CherryPick vs Black Cherry */
.ccfp .ways{border-top:1px solid var(--line)}
.ccfp .ways-head{text-align:center;max-width:660px;margin:0 auto 40px}
.ccfp .ways-head h2{font-size:clamp(30px,4.2vw,52px);margin-top:12px}
.ccfp .ways-head p{color:var(--muted);font-size:16px;margin-top:12px}
.ccfp .cw-dot{color:var(--cherry)}
.ccfp .pills{display:grid;grid-template-columns:1fr 1fr;gap:34px;max-width:980px;margin:0 auto}
.ccfp .way{display:flex;flex-direction:column;align-items:stretch;height:100%}
.ccfp .way .pill{flex:1 1 auto}
.ccfp .way-tag{align-self:center;font-family:var(--mono);font-size:14px;font-weight:800;letter-spacing:.16em;text-transform:uppercase;color:#fff;padding:8px 24px;border-radius:999px;margin-bottom:16px}
.ccfp .way-tag.red{background:var(--cherry);box-shadow:0 10px 26px -12px rgba(224,30,73,.8)}
.ccfp .way-tag.pl{background:#7c3aed;box-shadow:0 10px 26px -12px rgba(124,58,237,.8)}
.ccfp .pill{border:1px solid var(--line-2);border-radius:16px;padding:28px 26px;background:var(--surface);text-align:center;display:flex;flex-direction:column;align-items:center}
.ccfp .pill.red{border-color:rgba(236,43,87,.45);box-shadow:0 30px 70px -50px rgba(236,43,87,.7)}
.ccfp .pill.purple{border-color:rgba(139,92,246,.45);box-shadow:0 30px 70px -50px rgba(139,92,246,.7)}
.ccfp .pill h3{font-size:24px;margin:0 0 6px}
.ccfp .pill-kind{font-size:11px;letter-spacing:.06em;text-transform:uppercase;margin-bottom:12px}
.ccfp .pill-kind.red{color:var(--cherry)}
.ccfp .pill-kind.pl{color:#8b5cf6}
.ccfp .pill p{color:var(--muted);font-size:15px;margin:0 auto;max-width:320px;flex:1 1 auto}
.ccfp .pill-demo{margin-top:18px;width:100%}
@media (max-width:720px){.ccfp .pills{grid-template-columns:1fr}}

.ccfp .prem-cta{display:flex;gap:12px;justify-content:center;flex-wrap:wrap;margin-top:28px}
.ccfp .demo{background:var(--surface-2);border-top:1px solid var(--line);padding:72px 0}
.ccfp .demo-head{text-align:center;max-width:620px;margin:0 auto 32px}
.ccfp .demo-head h2{font-size:clamp(26px,3.4vw,40px);margin-top:12px}
.ccfp .demo-head p{color:var(--muted);font-size:16px;margin-top:12px}
.ccfp .demo-frame{position:relative;width:100%;max-width:900px;margin:0 auto;aspect-ratio:16/9;border-radius:16px;overflow:hidden;border:1px solid var(--line);box-shadow:var(--shadow);background:#000}
.ccfp .demo-frame iframe{position:absolute;inset:0;width:100%;height:100%;border:0;display:block}
.ccfp .demo-ph{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;text-align:center;padding:24px;background:var(--surface);border:2px dashed var(--line-2);border-radius:16px}
.ccfp .demo-ph-title{font-weight:700;font-size:18px;color:var(--ink)}
.ccfp .demo-ph-note{font-size:13px;color:var(--faint);font-family:var(--mono)}
.ccfp .drop{padding:70px 0;border-top:1px solid var(--line);background:var(--surface-2)}
.ccfp .drop-head{text-align:center;max-width:600px;margin:0 auto 30px}
.ccfp .drop-logo{display:block;margin:0 auto 16px}
.ccfp .drop-head h2{font-size:clamp(28px,3.8vw,44px);margin-top:14px}
.ccfp .drop-head p{color:var(--muted);font-size:17px;margin-top:12px}
/* The uploader (UploadDownloadArea) is a shared component built for the DARK app — its text/icon
   are hard-coded white, so on the light page it went invisible. Frame it in a dark card (like the
   ET embeds elsewhere on the page) so the white content reads in BOTH themes without touching the
   shared component. */
.ccfp .drop-box{width:100%;max-width:760px;margin:0 auto;background:#100d0c;border:1px solid rgba(255,255,255,0.07);border-radius:20px;padding:16px;box-shadow:var(--shadow)}
.ccfp .drop-demo{max-width:760px;margin:30px auto 0;display:flex;justify-content:center}

/* STEPS */
.ccfp .steps{border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.ccfp .steps-in{display:grid;grid-template-columns:repeat(3,1fr);gap:0}
.ccfp .step{padding:40px 26px;text-align:center;border-left:1px solid var(--line)}
.ccfp .step:first-child{border-left:none}
.ccfp .step .k{font-size:12px;color:var(--cherry);letter-spacing:.1em}
.ccfp .step h3{font-size:22px;margin:12px 0 6px}
.ccfp .step p{margin:0;color:var(--muted);font-size:14px}
@media (max-width:700px){ .ccfp .steps-in{grid-template-columns:1fr} .ccfp .step{border-left:none;border-top:1px solid var(--line)} .ccfp .step:first-child{border-top:none} }

/* PHONE CAROUSEL */
/* Claude + ChatGPT band — one line and the way to the /ai guide */
.ccfp .aiband{border-top:1px solid var(--line);background:var(--surface-2);padding:44px 0}
.ccfp .aiband-in{display:flex;align-items:center;justify-content:space-between;gap:28px;flex-wrap:wrap}
.ccfp .aiband h2{font-size:clamp(24px,3vw,34px);margin-top:10px}
.ccfp .aiband p{color:var(--muted);font-size:16px;margin:10px 0 0;max-width:56ch}
.ccfp .aiband .btn{flex:none}
@media (max-width:640px){ .ccfp .aiband .btn{width:100%;justify-content:center} }
.ccfp .phones{padding:84px 0}
.ccfp .ph-head{text-align:center;margin-bottom:34px}
.ccfp .ph-head h2{font-size:clamp(28px,3.8vw,44px);margin-top:12px}
.ccfp .ph-sub{color:var(--muted);font-size:17px;margin-top:12px}
.ccfp .crossed{color:var(--faint);text-decoration:line-through;text-decoration-color:var(--cherry);text-decoration-thickness:3px}
.ccfp .ph-stage{display:flex;align-items:center;justify-content:center;gap:14px;min-height:600px}
.ccfp .ph-slot{flex:1;max-width:760px;display:flex;justify-content:center;align-items:center}
.ccfp .ph-arrow{flex:none;width:46px;height:46px;border-radius:50%;border:1px solid var(--line-2);background:var(--surface);color:var(--ink);font-size:22px;line-height:1;cursor:pointer;transition:border-color .2s ease, color .2s ease;display:grid;place-items:center}
.ccfp .ph-arrow:hover{border-color:var(--cherry);color:var(--cherry)}
.ccfp .ph-copy{text-align:center;color:var(--muted);font-size:16px;max-width:460px;margin:24px auto 0;min-height:24px}
.ccfp .ph-dots{display:flex;justify-content:center;gap:8px;margin-top:16px;flex-wrap:wrap}
.ccfp .pdot{font-family:var(--mono);font-size:12px;color:var(--muted);border:1px solid var(--line-2);background:transparent;border-radius:999px;padding:7px 14px;cursor:pointer;transition:all .18s ease}
.ccfp .pdot.on{color:#fff;background:var(--cherry);border-color:var(--cherry)}
.ccfp .pdot:not(.on):hover{border-color:var(--cherry);color:var(--ink)}
@media (max-width:600px){ .ccfp .ph-arrow{width:40px;height:40px} }

/* PREMIERE section */
.ccfp .prem{padding:80px 0;border-top:1px solid var(--line);background:var(--surface-2)}
.ccfp .prem-lockup{display:flex;align-items:center;gap:20px;margin-bottom:24px}
.ccfp .prem-lockup .plus{font-size:40px;color:var(--faint);font-weight:300}
.ccfp .prlogo{display:inline-grid;place-items:center;width:58px;height:58px;border-radius:13px;background:#2b0a52;color:#c9b3ff;font-weight:800;font-size:26px;letter-spacing:-.02em;border:1px solid #4a2a7a}

/* phone → Premiere stage */
.ccfp .prem-stage{display:flex;align-items:center;justify-content:center;gap:20px;flex-wrap:wrap}
.ccfp .prem-phone{width:250px;flex:none;border-radius:26px;border:9px solid #15131b;background:#0b0a0f;padding:16px 12px 14px;position:relative;box-shadow:var(--shadow)}
.ccfp .pp-notch{width:74px;height:16px;background:#15131b;border-radius:0 0 12px 12px;margin:-16px auto 12px}
.ccfp .pp-head{display:flex;align-items:center;gap:6px;font-size:9px;letter-spacing:.14em;color:#8a7d84;margin-bottom:10px}
.ccfp .pp-head img{display:block}
.ccfp .pp-row{display:flex;align-items:center;gap:8px;font-size:12px;color:#a99ca2;line-height:1.3;padding:9px 8px;border-radius:8px;margin-bottom:6px}
.ccfp .pp-row.on{background:rgba(236,43,87,.14);color:#f5edf0}
.ccfp .pp-cbx{width:17px;height:17px;border-radius:5px;border:1.5px solid #38292f;flex:none;display:grid;place-items:center;background:#0f0a0d}
.ccfp .pp-row.on .pp-cbx{background:#ec2b57;border-color:#ec2b57}
.ccfp .pp-cbx svg{width:10px;height:10px;stroke:#fff;fill:none;display:block}
.ccfp .pp-txt{flex:1;min-width:0}
.ccfp .pp-n{color:#ff9db4;font-weight:700;flex:none}
.ccfp .pp-tap{text-align:center;font-size:10px;color:#ff6f8f;margin-top:8px;letter-spacing:.06em}
.ccfp .prem-phone-real{flex:none;display:flex;flex-direction:column;align-items:center;gap:10px}
.ccfp .prem-phone-tap{font-size:11px;color:var(--cherry);letter-spacing:.04em}
.ccfp .prem-arrow{flex:none;font-size:34px;color:var(--cherry);font-weight:300}
.ccfp .prem-screen{flex:1;min-width:300px;max-width:720px}
.ccfp .prem-realimg{width:100%;height:auto;display:block;border-radius:12px;border:1px solid var(--line);box-shadow:var(--shadow)}
.ccfp .prem-tabs{display:flex;justify-content:center;gap:8px;margin-bottom:24px}
.ccfp .prem-tab{font-family:var(--sans);font-size:13px;font-weight:650;color:var(--muted);background:var(--surface);border:1px solid var(--line-2);border-radius:999px;padding:9px 18px;cursor:pointer;transition:transform .14s ease, background .2s ease, border-color .2s ease, color .2s ease}
.ccfp .prem-tab.on{color:#fff;background:var(--cherry);border-color:var(--cherry)}
.ccfp .prem-tab:not(.on):hover{border-color:var(--cherry);color:var(--ink)}
.ccfp .prem-live{max-width:960px;margin:0 auto}
.ccfp .prem-screen-wide{max-width:1000px;margin:0 auto}
.ccfp .prem-linkbar{display:flex;align-items:center;gap:8px;max-width:640px;margin:20px auto 0;padding:8px 10px;background:#1a1130;border:1px solid #3a2a5a;border-radius:9px}
.ccfp .pll-lbl{font-size:11px;color:#c9b8ff;white-space:nowrap}
.ccfp .pll-in{flex:1;min-width:0;font-size:11px;padding:6px 8px;background:#0f0a1e;color:#e8e0ff;border:1px solid #3a2a5a;border-radius:5px}
.ccfp .pll-copy{flex:none;font-size:11px;font-weight:700;color:#fff;background:#7b4bd8;border:none;border-radius:5px;padding:7px 14px;cursor:pointer}
.ccfp .pll-copy:hover{background:#8f63e6}
.ccfp .prem-linknote{text-align:center;color:var(--faint);font-size:11px;margin-top:8px}
@media (max-width:760px){ .ccfp .prem-arrow{transform:rotate(90deg)} .ccfp .prem-phone{width:220px} }
.ccfp .prem .sec-head{max-width:620px;margin-bottom:34px}
.ccfp .prem .sec-head h2{font-size:clamp(28px,3.8vw,44px);margin-top:14px}
.ccfp .prem .sec-head p{color:var(--muted);font-size:17px;margin-top:14px}
.ccfp .prem-note{text-align:center;color:var(--faint);font-size:12px;margin-top:16px}

/* Premiere window recreation (hard-coded dark, reads like a screenshot in any theme) */
.ccfp .pw{border-radius:12px;overflow:hidden;border:1px solid #000;background:#1c1c1e;box-shadow:0 40px 90px -50px rgba(0,0,0,.9)}
.ccfp .pw-bar{display:flex;align-items:center;gap:12px;padding:9px 14px;background:#2b2b2e;border-bottom:1px solid #000;font-family:var(--mono);font-size:11px;color:#b9b9bf}
.ccfp .pw-dots{display:flex;gap:6px}
.ccfp .pw-dots i{width:10px;height:10px;border-radius:50%}
.ccfp .pw-dots i:nth-child(1){background:#ff5f57}.ccfp .pw-dots i:nth-child(2){background:#febc2e}.ccfp .pw-dots i:nth-child(3){background:#28c840}
.ccfp .pw-top{display:grid;grid-template-columns:1.4fr 1fr;gap:2px;background:#000}
.ccfp .pw-panel{background:#120d11;padding:12px}
.ccfp .pw-panel-head{display:flex;align-items:center;gap:8px;padding-bottom:10px;border-bottom:1px solid #2a2025;margin-bottom:10px;color:#f3f4f9;font-size:14px}
.ccfp .pw-panel-head img{display:block}
.ccfp .pw-panel-head b{font-weight:750}
.ccfp .pw-phone-tag{margin-left:auto;font-size:10px;color:#c9b8ff;border:1px solid #3a2a5a;border-radius:999px;padding:3px 8px}
.ccfp .pw-toolbar{display:flex;align-items:center;gap:5px;flex-wrap:wrap;padding-bottom:10px;margin-bottom:8px;border-bottom:1px solid #211a1f}
.ccfp .pw-chip{font-family:var(--mono);font-size:10px;color:#b7adb3;border:1px solid #3a2f35;border-radius:5px;padding:3px 7px;white-space:nowrap}
.ccfp .pw-chip.pos{color:#4bbf87;border-color:#2b5a44}
.ccfp .pw-chip.neu{color:#9aa0b4;border-color:#3a3f4a}
.ccfp .pw-chip.neg{color:#ff6f8f;border-color:#5a2b3a}
.ccfp .pw-chip.srch{color:#f3f4f9;background:#241a20;border-color:#4a3a40}
.ccfp .pw-vsep{width:1px;height:14px;background:#2a2025;margin:0 3px}
.ccfp .pw-row{display:flex;align-items:center;gap:10px;padding:8px;border-radius:7px;margin-bottom:5px}
.ccfp .pw-row.on{background:rgba(236,43,87,.16);box-shadow:inset 2px 0 0 #ec2b57}
.ccfp .pw-cbx{width:17px;height:17px;border-radius:5px;border:1.5px solid #4a3a40;flex:none;display:grid;place-items:center;background:#0f0a0d}
.ccfp .pw-row.on .pw-cbx{background:#ec2b57;border-color:#ec2b57}
.ccfp .pw-cbx svg{width:10px;height:10px;stroke:#fff;fill:none;display:block}
.ccfp .pw-thumb{width:44px;height:26px;border-radius:4px;flex:none;background:linear-gradient(135deg,#3a2130,#171018)}
.ccfp .pw-txt{font-size:12.5px;color:#c9c2c6;line-height:1.3;flex:1;min-width:0}
.ccfp .pw-row.on .pw-txt{color:#f3f4f9}
.ccfp .pw-tc{display:flex;align-items:center;gap:8px;font-size:10px;color:#8a8088;flex:none}
.ccfp .pw-ord{color:#ff9db4;font-weight:700}
.ccfp .pw-link{display:flex;align-items:center;gap:8px;margin-top:10px;padding:8px 10px;background:#1a1130;border:1px solid #3a2a5a;border-radius:8px}
.ccfp .pw-link-lbl{font-size:11px;color:#c9b8ff;white-space:nowrap}
.ccfp .pw-link-in{flex:1;min-width:0;font-size:11px;padding:5px 7px;background:#0f0a1e;color:#e8e0ff;border:1px solid #3a2a5a;border-radius:5px}
.ccfp .pw-link-copy{flex:none;font-size:11px;font-weight:700;color:#fff;background:#7b4bd8;border:none;border-radius:5px;padding:6px 12px;cursor:pointer}
.ccfp .pw-link-copy:hover{background:#8f63e6}
.ccfp .pw-monitor{background:#0b0b0c;display:flex;flex-direction:column}
.ccfp .pw-mon-lbl{font-size:10px;color:#8a8a90;padding:8px 12px;border-bottom:1px solid #1a1a1c}
.ccfp .pw-mon-frame{flex:1;margin:14px;border-radius:4px;background:radial-gradient(120% 90% at 50% 40%, #241019, #060406);min-height:130px}
.ccfp .pw-tl{background:#232326;border-top:1px solid #000;padding:10px 12px}
.ccfp .pw-tl-head{font-size:10px;color:#9a9aa0;margin-bottom:6px}
.ccfp .pw-ruler{display:flex;justify-content:space-between;font-size:8.5px;color:#6a6a70;padding:0 0 6px 30px;border-bottom:1px solid #333;margin-bottom:8px}
.ccfp .pw-tl-body{position:relative}
.ccfp .pw-playhead{position:absolute;top:-4px;bottom:-4px;left:56%;width:1.5px;background:#4a9fff;z-index:2}
.ccfp .pw-playhead::before{content:"";position:absolute;top:-4px;left:-4px;border:5px solid transparent;border-top-color:#4a9fff}
.ccfp .pw-track{display:flex;align-items:center;gap:8px;margin-bottom:6px}
.ccfp .pw-track-l{font-size:10px;color:#8a8a90;width:22px;flex:none}
.ccfp .pw-clips{display:flex;gap:4px;flex:1;min-width:0;overflow:hidden}
.ccfp .pw-clip{height:28px;border-radius:4px;flex:none;background:linear-gradient(180deg,#5a1e30,#3a1420);border:1px solid #7a2a3e}
.ccfp .pw-clip.wave{background:repeating-linear-gradient(90deg,#5a1e30 0 2px,#8a3050 2px 4px)}
.ccfp .pw-clip.hot{border-color:#ec2b57;box-shadow:0 0 0 1px #ec2b57, 0 0 14px rgba(236,43,87,.5)}
@media (max-width:760px){ .ccfp .pw-top{grid-template-columns:1fr} .ccfp .pw-monitor{display:none} }

/* PRICING */
.ccfp .price{padding:80px 0 96px;text-align:center;border-top:1px solid var(--line)}
.ccfp .price-h{margin-top:14px;font-size:clamp(28px,3.8vw,44px)}
.ccfp .cards{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;max-width:1040px;margin:44px auto 0;text-align:left}
.ccfp .card{background:var(--surface);border:1px solid var(--line);border-radius:18px;padding:30px}
.ccfp .card.pro{border-color:var(--cherry);box-shadow:0 0 0 1px var(--cherry), var(--shadow)}
.ccfp .card .plan{font-size:12px;letter-spacing:.16em;text-transform:uppercase;color:var(--faint)}
.ccfp .card .amt{font-size:42px;font-weight:780;letter-spacing:-.03em;margin:12px 0 2px}
.ccfp .card .amt span{font-size:15px;font-weight:500;color:var(--faint)}
.ccfp .card .desc{color:var(--muted);font-size:14px;margin-bottom:20px}
.ccfp .card ul{list-style:none;margin:0 0 24px;padding:0;display:flex;flex-direction:column;gap:10px}
.ccfp .card li{display:flex;gap:9px;font-size:14px;color:var(--muted);align-items:flex-start}
.ccfp .card li svg{width:15px;height:15px;stroke:var(--cherry);fill:none;flex:none;margin-top:2px;display:block}
.ccfp .card .btn{width:100%;justify-content:center}
@media (max-width:900px){ .ccfp .cards{grid-template-columns:1fr;max-width:420px} }

/* CLOSE + FOOTER */
.ccfp .close{text-align:center;padding:20px 0 90px}
.ccfp .close h2{font-size:clamp(30px,4.4vw,52px)}
.ccfp .close p{color:var(--muted);font-size:17px;margin:16px 0 28px}
.ccfp footer{border-top:1px solid var(--line);padding:34px 0}
.ccfp .foot{display:flex;align-items:center;gap:20px;flex-wrap:wrap}
.ccfp .foot .fl{margin-left:auto;display:flex;gap:22px;font-family:var(--mono);font-size:12px;color:var(--faint);flex-wrap:wrap}
.ccfp .foot .fl .linkbtn{color:var(--faint)}
.ccfp .foot .fl .linkbtn:hover{color:var(--muted)}
`;

export default observer(FlagshipPage);
